import * as amplitude from "@amplitude/analytics-browser";
import { Skeleton } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { SearchSelect } from "src/components/Inputs/SearchSelect";
import { Item } from "src/components/Inputs/SearchSelect/SearchSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useGlossary } from "src/hooks/useGlossary";
import * as RD from "src/types/remoteData";

export const AllSchoolsKey = "all-schools";

type Props = {
  enrollmentPeriodId: string;
  schoolId: string;
  onChange: (item: Item | null) => void | Promise<void>;
  hideAllSchools?: boolean;
};

export const SchoolSearchSelect: React.FC<Props> = ({
  enrollmentPeriodId,
  schoolId,
  onChange,
  hideAllSchools = false,
}) => {
  const { glossary } = useGlossary();

  const schoolsQuery = FSQ.useGetSchoolsByEnrollmentPeriod({
    path: {
      enrollmentPeriodId,
    },
  });
  const schoolsRD = useMemo(() => {
    return RD.fromRestResult(schoolsQuery).map(({ data }) => {
      const schools = data.map((school) => ({
        key: school.id,
        label: school.name,
      }));

      if (hideAllSchools) {
        return schools;
      }
      return schools.length > 1
        ? [{ key: AllSchoolsKey, label: "All schools" }, ...schools]
        : schools;
    });
  }, [hideAllSchools, schoolsQuery]);

  const selectedSchoolRD = useMemo(() => {
    return schoolsRD.map((schools) => {
      return schools.find((s) => s.key === schoolId);
    });
  }, [schoolId, schoolsRD]);

  return (
    <RemoteDataView
      remoteData={RD.toTuple(schoolsRD, selectedSchoolRD)}
      error={(error) => <GenericError error={error as Error} />}
      loading={<Skeleton width="100%" height={10} marginTop={2} />}
    >
      {([schoolsData, selectedSchool]) => (
        <SearchSelect
          variant="unlocked"
          label=""
          formLabelProps={{ "aria-label": "Select school" }}
          items={schoolsData}
          placeholder={glossary`Select school`}
          selectedItem={
            selectedSchool
              ? { key: selectedSchool.key, label: selectedSchool.label }
              : null
          }
          onChange={(item) => {
            amplitude.track("Toggled capacity view", {
              capacity_toggle:
                item === null || item.key === AllSchoolsKey
                  ? "capacity_summary"
                  : "capacity_school",
            });

            return onChange(item);
          }}
        />
      )}
    </RemoteDataView>
  );
};
